import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, BookOpen, Compass, Printer, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { getSections, visibleQuestions, formatAnswer } from "@/data/questionnaire";
import { businessForAi, belongsToCurrent, getBusiness } from "@/data/business";
import { computeStrategy, strategyToText } from "@/data/strategy";
import { staffToText } from "@/data/staff";
import { loadAll, loadMethods, upsertMethod, removeMethod, loadWeights, formatDate } from "@/lib/storage";
import { streamMethod } from "@/lib/reportStream";
import { EmailButton } from "@/components/EmailButton";
import { HandoffButton } from "@/components/HandoffButton";

const submissionToText = (sub) => {
  const answers = getSections(sub.businessType).map((s) =>
    `[${s.title}]\n` + visibleQuestions(s, sub.answers).map((q) => `- ${q.text}\n  Risposta: ${formatAnswer(q, sub.answers[q.id]) || "(non risposto)"}`).join("\n")
  ).join("\n\n");
  const strat = strategyToText(computeStrategy(sub.answers, loadWeights(), sub.businessType));
  const staff = staffToText(sub);
  return `${answers}\n\nClassificazione automatica delle azioni:\n${strat}${staff ? `\n\nRisposte dello staff:\n${staff}` : ""}`;
};

export default function MethodPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const completed = loadAll().filter((s) => s.status === "completed" && belongsToCurrent(s));
  const methods0 = loadMethods().filter(belongsToCurrent);
  const preset = completed.find((s) => s.id === params.get("da"));
  const [methods, setMethods] = useState(methods0);
  const [current, setCurrent] = useState(() => (id ? methods0.find((m) => m.id === id) : null));
  const [source, setSource] = useState(() => (preset ? submissionToText(preset) : ""));
  const [notes, setNotes] = useState(preset?.notes || "");
  const [respondent, setRespondent] = useState(preset?.respondent || "");
  const [picked, setPicked] = useState(preset?.id || "");
  const [streaming, setStreaming] = useState(false);
  const abort = useRef(null);

  useEffect(() => () => abort.current?.abort(), []);

  const pick = (subId) => {
    setPicked(subId);
    const sub = completed.find((s) => s.id === subId);
    if (sub) {
      setSource(submissionToText(sub));
      setRespondent(sub.respondent);
      setNotes(sub.notes || "");
    }
  };

  const build = (baseline = false) => {
    const src = baseline ? "VERSIONE_BASE" : source;
    if (!src.trim()) return;
    const m = { id: crypto.randomUUID(), respondent: baseline ? "Versione base (senza risposte)" : respondent || "Titolari", createdAt: new Date().toISOString(), source: src, notes: baseline ? "" : notes, text: "", done: false, baseline, businessId: getBusiness().id };
    setCurrent(m);
    setStreaming(true);
    let text = "";
    abort.current = new AbortController();
    streamMethod({ respondent: m.respondent, source: src, notes: m.notes, business: businessForAi() }, {
      signal: abort.current.signal,
      onToken: (t) => {
        text += t;
        setCurrent((c) => ({ ...c, text }));
      },
      onDone: () => {
        setStreaming(false);
        const saved = upsertMethod({ ...m, text, done: true });
        setCurrent(saved);
        setMethods(loadMethods().filter(belongsToCurrent));
        navigate(`/metodo/${saved.id}`, { replace: true });
      },
      onError: (e) => {
        setStreaming(false);
        toast.error(`Metodo non generato: ${e.message}`);
      },
    });
  };

  const del = (mid) => {
    if (!window.confirm("Eliminare questo metodo?")) return;
    removeMethod(mid);
    setMethods(loadMethods().filter(belongsToCurrent));
    if (current?.id === mid) {
      setCurrent(null);
      navigate("/metodo", { replace: true });
    }
  };

  const methodDoc = () => ({ kind: "method", title: current.respondent, subtitle: `Metodo operativo · ${formatDate(current.createdAt)}`, blocks: [{ markdown: current.text }] });

  return (
    <Shell wide header={
      <div className="flex gap-3 no-print">
        <button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/")} data-testid="method-back-btn"><ArrowLeft size={16} /> Elenco</button>
        {current?.text && !streaming && <EmailButton className="btn btn-ghost !min-h-[44px] text-sm" build={methodDoc} />}
        {current?.text && !streaming && <HandoffButton className="btn btn-ghost !min-h-[44px] text-sm" build={methodDoc} />}
        {current?.text && <button className="btn btn-olive !min-h-[44px] text-sm" onClick={() => window.print()} data-testid="method-print-btn"><Printer size={16} /> Stampa / PDF</button>}
      </div>
    }>
      {!current && (
        <div className="fade-up">
          <p className="eyebrow">Dalle risposte al metodo</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">Metodo operativo e impostazione della struttura</h1>
          <p className="text-[#5C4D46] mt-3 max-w-2xl">Scegli una compilazione già fatta oppure incolla le risposte dei titolari (anche in forma libera o copiate dal modulo cartaceo). L'AI costruisce diagnosi, metodo, impostazione degli spazi e dell'offerta, transizione cucina, calendario a 4 settimane / 3 mesi / 12 mesi. Non hai ancora le risposte? Parti dalla <strong>versione base</strong>: un metodo fondato su ciò che già si sa del Casale, con i punti da verificare con i titolari.</p>

          <div className="card p-6 sm:p-9 mt-8 grid gap-6">
            {completed.length > 0 && (
              <label className="block">
                <span className="eyebrow">Parti da una compilazione</span>
                <select className="field mt-2" value={picked} onChange={(e) => pick(e.target.value)} data-testid="method-pick-submission">
                  <option value="">— scegli —</option>
                  {completed.map((s) => <option key={s.id} value={s.id}>{s.respondent || "Senza nome"} · {formatDate(s.completedAt || s.createdAt)}</option>)}
                </select>
              </label>
            )}
            <label className="block">
              <span className="eyebrow">Chi ha risposto</span>
              <input className="field mt-2" value={respondent} onChange={(e) => setRespondent(e.target.value)} placeholder="es. Maria e Giovanni" data-testid="method-respondent-input" />
            </label>
            <label className="block">
              <span className="eyebrow">Risposte dei titolari</span>
              <textarea className="field mt-2 min-h-[260px] font-mono text-sm leading-relaxed" value={source} onChange={(e) => setSource(e.target.value)}
                placeholder={"Incolla qui le risposte, ad esempio:\n1. Circa 60 eventi l'anno\n2. Matrimoni\n3. Sala, prato, piscina\n4. Funziona il passaparola, non funziona la gestione dei preventivi…"} data-testid="method-source-input" />
            </label>
            <label className="block">
              <span className="eyebrow">Note del consulente (facoltative)</span>
              <textarea className="field mt-2 min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Impressioni dall'incontro, vincoli, cose dette a voce…" data-testid="method-notes-input" />
            </label>
            <div className="flex flex-wrap justify-between items-center gap-3">
              <button className="btn btn-ghost" onClick={() => build(true)} disabled={streaming} data-testid="method-baseline-btn">
                <Compass size={18} /> Versione base, senza risposte
              </button>
              <button className="btn btn-primary text-lg !min-h-[64px] !px-9" onClick={() => build(false)} disabled={!source.trim() || streaming} data-testid="method-build-btn">
                <Wand2 size={20} /> Costruisci il metodo
              </button>
            </div>
          </div>

          {methods.length > 0 && (
            <section className="mt-12">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B] mb-4">Metodi già costruiti</h2>
              <div className="grid gap-3" data-testid="methods-list">
                {methods.map((m) => (
                  <div key={m.id} className="card p-4 sm:p-5 flex items-center gap-4" data-testid={`method-row-${m.id}`}>
                    <BookOpen className="text-[#2D4030] flex-none" size={22} />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-xl font-semibold text-[#2C221E] truncate">{m.respondent}</p>
                      <p className="text-sm text-[#7A6B63]">{formatDate(m.createdAt)}</p>
                    </div>
                    <button className="btn btn-ghost !min-h-[44px] !px-3" onClick={() => del(m.id)} aria-label="Elimina" data-testid="method-delete-btn"><Trash2 size={16} /></button>
                    <button className="btn btn-olive !min-h-[44px]" onClick={() => { setCurrent(m); navigate(`/metodo/${m.id}`); }} data-testid="method-open-btn">Apri</button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {current && (
        <article className="fade-up" data-testid="method-document">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">Metodo operativo</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2" data-testid="method-respondent">{current.respondent}</h1>
              <p className="text-sm text-[#7A6B63] mt-2">{formatDate(current.createdAt)}{streaming && <span className="ml-3" data-testid="method-streaming-label">· in scrittura…</span>}</p>
            </div>
            {!streaming && (
              <button className="btn btn-ghost no-print" onClick={() => { setCurrent(null); navigate("/metodo", { replace: true }); }} data-testid="method-new-btn">Nuovo metodo</button>
            )}
          </div>
          <div className={`card p-6 sm:p-10 mt-8 report ${streaming ? "cursor-blink" : ""}`} data-testid="method-content">
            {!current.text && streaming && <p className="text-[#7A6B63]">Sto leggendo le risposte</p>}
            <ReactMarkdown>{current.text}</ReactMarkdown>
          </div>
        </article>
      )}
    </Shell>
  );
}
